import { Formik, ErrorMessage, useFormikContext, Field } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import treeFormModel from "./FormModel/treeFormModel";
import { validation } from "./FormModel/validationSchema";
import { useMutation, useQueryClient } from "react-query";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import { Modal, Form, ModalBody, Button, Row, Col } from "react-bootstrap";
import useChoices from "@hooks/useChoices";
import { createTree, updateTree } from "../../../services/treeCrud";
import { MoreInfo, TreeProfile, TreeLocation, TreeSensor, Soil } from "./steps";
import { formInitialValues, formUpdateValues } from "./FormModel/initialValues";
import { useBackgroundTreeSpecies } from "@hooks/useBackgroundData";
import { styles } from "@components/styles";
import FormError from "@components/Form/FormError";
import ChildTree from "./steps/ChildTree";
import InfoBox from "@components/InfoBox/InfoBox";
import Tree from "../../Tree";

const stepAdd = ["tree_form.tree_profile", "tree_form.position", "tree_form.more_info"];
const stepEdit = [
  "tree_form.tree_profile",
  "tree_form.position",
  "tree_form.sensors",
  "tree_form.more_info",
];
const TREE_LIMIT_ERROR = "Tree limit exceeded for your package";
const { formField } = treeFormModel;
const currentYear = new Date().getFullYear();
const generateYearOptions = () => {
  const years = [];
  for (let year = currentYear; year >= 1000; year--) {
    years.push(year);
  }
  return years;
};

const getTreeStepInfo = (activeStep, isUpdate, t) => {
  const addMap = {
    0: {
      title: t("tree_form_info.add_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form_info.profile.heading")}</strong>
          </p>
          <ol>
            <li>
              <strong>{t("tree_form_info.profile.cadastre_label")}:</strong>{" "}
              {t("tree_form_info.profile.cadastre_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.profile.name_label")}:</strong>{" "}
              {t("tree_form_info.profile.name_help")}
            </li>
          </ol>
          <em>{t("tree_form_info.profile.note")}</em>
        </>
      ),
    },
    1: {
      title: t("tree_form_info.add_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form_info.position.heading")}</strong>
          </p>
          <ol>
            <li>
              <strong>{t("tree_form_info.position.latlng_label")}:</strong>{" "}
              {t("tree_form_info.position.latlng_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.position.altitude_label")}:</strong>{" "}
              {t("tree_form_info.position.altitude_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.position.map_label")}:</strong>{" "}
              {t("tree_form_info.position.map_help")}
            </li>
          </ol>
          <em>{t("tree_form_info.position.note")}</em>
        </>
      ),
    },
    2: {
      title: t("tree_form_info.add_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form_info.more.heading")}</strong>
          </p>
          <ol>
            <li>
              <strong>{t("tree_form_info.more.phase_label")}:</strong>{" "}
              {t("tree_form_info.more.phase_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.more.year_label")}:</strong>{" "}
              {t("tree_form_info.more.year_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.more.species_label")}:</strong>{" "}
              {t("tree_form_info.more.species_help")}
            </li>
            <li>
              <strong>{t("tree_form_info.more.metrics_label")}:</strong>{" "}
              {t("tree_form_info.more.metrics_help")}
            </li>
          </ol>
          <em>{t("tree_form_info.more.note")}</em>
        </>
      ),
    },
  };

  const editMap = {
    0: addMap[0],
    1: addMap[1],
    2: {
      title: t("tree_form_info.edit_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form.info.sensors.heading")}</strong>
          </p>
          <p>{t("tree_form.info.sensors.help")}</p>
          <em>{t("tree_form.info.sensors.note")}</em>
        </>
      ),
    },
    3: {
      title: t("tree_form_info.edit_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form.info.represent.heading")}</strong>
          </p>
          <p>{t("tree_form.info.represent.help")}</p>
        </>
      ),
    },
    4: addMap[2],
    5: {
      title: t("tree_form_info.edit_title"),
      content: (
        <>
          <p>
            <strong>{t("tree_form.info.soil.heading")}</strong>
          </p>
          <ol>
            <li>
              <strong>{t("tree_form.info.soil.pwp_label")}</strong>{" "}
              {t("tree_form.info.soil.pwp_help")}
            </li>
            <li>
              <strong>{t("tree_form.info.soil.max_nf_label")}</strong>{" "}
              {t("tree_form.info.soil.max_nf_help")}
            </li>
            <li>
              <strong>{t("tree_form.info.soil.max_f_label")}</strong>{" "}
              {t("tree_form.info.soil.max_f_help")}
            </li>
          </ol>
          <em>{t("tree_form.info.soil.note")}</em>
        </>
      ),
    },
  };

  const map = isUpdate ? editMap : addMap;
  return (
    map[activeStep] ?? { title: t("tree_form_info.add_title"), content: <>{t("not_found")}</> }
  );
};

function TreeDetailInlineFields({ species }) {
  const { t } = useTranslation();
  const [phases] = useChoices("growth_phase");
  const [edit, setEdit] = useState(false);
  const [soilEdit, setSoilEdit] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [showData, setShowData] = useState(false);
  const previewTree = useMemo(
    () => ({
      installations: [],
      soil: null,
      blue_threshold: 0,
      green_threshold: 0,
      yellow_threshold: 0,
      yellow_treshold: 0,
      health_state: 3,
    }),
    []
  );
  return (
    <div className="tree-form-inline-detail d-flex flex-column gap-3">
      <div className="d-flex justify-content-center">
        <Tree
          tree={previewTree}
          edit={edit}
          setEdit={setEdit}
          soilEdit={soilEdit}
          setSoilEdit={setSoilEdit}
          selectedInstallation={selectedInstallation}
          setSelectedInstallation={setSelectedInstallation}
          showData={showData}
          setShowData={setShowData}
          hideEditButtons={true}
        />
      </div>
      <h3 className="tree-form-inline-detail__title mb-2">{t("tree")}</h3>
      <Row data-testid="tree-detail-form-row" className="g-2">
        <Col lg={6} className="pe-lg-1">
          <ul className="mb-0 list-group">
            <li className="list-group-item">
              <Form.Label className="mb-1">
                {t(formField.treeCadastreNo.label)} <span className="text-danger">*</span>
              </Form.Label>
              <Field
                type="text"
                name={formField.treeCadastreNo.name}
                className="form-control form-control-sm"
                maxLength={24}
                placeholder={t("maximum_of") + " 24 " + t("characters")}
              />
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">
                {t(formField.name.label)} <span className="text-danger">*</span>
              </Form.Label>
              <Field
                type="text"
                name={formField.name.name}
                className="form-control form-control-sm"
                maxLength={40}
                placeholder={t("maximum_of") + " 40 " + t("characters")}
              />
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">{t("planting_year")}</Form.Label>
              <Field
                as="select"
                className="form-control form-select form-select-sm"
                name="planting_year"
              >
                <option value="">{t("select_year")}</option>
                {generateYearOptions().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Field>
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">{t(formField.phase.label)}</Form.Label>
              <Field as="select" className="form-control form-select form-select-sm" name="phase">
                <option value="">{t("select_phase")}</option>
                {phases?.map((phase) => (
                  <option key={phase.key} value={phase.key}>
                    {t(phase.value)}
                  </option>
                ))}
              </Field>
            </li>
            <li className="list-group-item py-1 border-0">
              <small className="text-muted">{t("validation.required_fields")}</small>
            </li>
          </ul>
        </Col>
        <Col lg={6} className="ps-lg-1">
          <ul className="mb-0 list-group">
            <li className="list-group-item">
              <Form.Label className="mb-1">
                {t(formField.specie.label)} <span className="text-danger">*</span>
              </Form.Label>
              <Field as="select" className="form-control form-select form-select-sm" name="species">
                <option value="">
                  {t("latin_name")} | {t("german_name")}
                </option>
                {species?.map((speciesItem) => (
                  <option key={speciesItem.id} value={speciesItem.id}>
                    {`${speciesItem.name} | ${speciesItem.de_name}`}
                  </option>
                ))}
              </Field>
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">{t(formField.trunkCircumference.label)}</Form.Label>
              <Field
                type="number"
                name={formField.trunkCircumference.name}
                className="form-control form-control-sm"
                placeholder={`${t("trunk_circumference")} in cm`}
              />
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">{t(formField.height.label)}</Form.Label>
              <Field
                type="number"
                name={formField.height.name}
                className="form-control form-control-sm"
                placeholder={`${t("height")} in m`}
                min="0"
              />
            </li>
            <li className="list-group-item">
              <Form.Label className="mb-1">{t(formField.crownDiameter.label)}</Form.Label>
              <Field
                type="number"
                name={formField.crownDiameter.name}
                className="form-control form-control-sm"
                placeholder={`${t("crown_diameter")} in m`}
              />
            </li>
          </ul>
        </Col>
      </Row>
    </div>
  );
}

const renderStepAdd = (steps, values, formValue, setFieldValue, species, positionReadOnly) => {
  switch (steps) {
    case 0:
      return <TreeProfile formField={formField} setFieldValue={setFieldValue} />;
    case 1:
      return (
        <TreeLocation
          values={values}
          formValue={formValue}
          formField={formField}
          validation={validation}
          setFieldValue={setFieldValue}
          readOnly={positionReadOnly}
        />
      );

    case 2:
      return <MoreInfo formField={formField} species={species} />;
    case 3:
      return <Soil formField={formField} />;
    default:
      return <>NotFound</>;
  }
};
const renderStepEdit = (steps, values, formValue, setFieldValue, species, positionReadOnly) => {
  switch (steps) {
    case 0:
      return <TreeProfile formField={formField} />;
    case 1:
      return (
        <TreeLocation
          values={values}
          formField={formField}
          validation={validation}
          setFieldValue={setFieldValue}
          readOnly={positionReadOnly}
        />
      );
    case 2:
      return (
        <TreeSensor
          formField={formField}
          values={values}
          formValue={formValue}
          setFieldValue={setFieldValue}
        />
      );
    case 3:
      return <MoreInfo formField={formField} species={species} />;
    case 4:
      return <Soil formField={formField} />;
    default:
      return <>NotFound</>;
  }
};

function PositionSync({ position }) {
  const { setFieldValue, values } = useFormikContext();
  const lastPosition = useRef(null);

  useEffect(() => {
    if (!position) return;
    const nextLat = position.lat ?? position.latitude;
    const nextLng = position.lng ?? position.longitude;
    if (nextLat == null || nextLng == null) return;

    const last = lastPosition.current;
    if (last && String(last.lat) === String(nextLat) && String(last.lng) === String(nextLng)) {
      return;
    }

    lastPosition.current = { lat: nextLat, lng: nextLng };

    if (String(values.latitude) !== String(nextLat)) {
      setFieldValue("latitude", nextLat);
    }
    if (String(values.longitude) !== String(nextLng)) {
      setFieldValue("longitude", nextLng);
    }
  }, [position?.lat, position?.lng, position?.latitude, position?.longitude, setFieldValue]);

  return null;
}

function PositionReporter({ onChange }) {
  const { values } = useFormikContext();

  useEffect(() => {
    if (!onChange) return;
    const nextLat = Number(values.latitude);
    const nextLng = Number(values.longitude);
    if (Number.isNaN(nextLat) || Number.isNaN(nextLng)) return;
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;

    onChange({ lat: nextLat, lng: nextLng });
  }, [onChange, values.latitude, values.longitude]);

  return null;
}

export default function TreeForm({
  show,
  isUpdate,
  setOpenForm,
  formValue,
  setIsUpdate,
  setTreeValues,
  initialValues,
  externalPosition,
  onPositionChange,
  renderMode = "modal",
  inlineVariant = "form",
  onCancel,
  positionReadOnly = false,
  initialStep = null,
  isolatedStep = null,
  areaId = null,
  topContent = null,
  renderTopContent = null,
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const query = new URLSearchParams(useLocation().search);
  const tid = query.get("selectedTree");
  // Use cached tree species from background loading
  const { treeSpecies: species } = useBackgroundTreeSpecies();
  const [error, setError] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const isInline = renderMode === "inline";
  const isDetailInline = isInline && inlineVariant === "detail";
  const isFlatMode = isInline && isolatedStep == null && !isDetailInline && !isUpdate;
  const isolatedStepIndex =
    isolatedStep === "sensors"
      ? 2
      : isolatedStep === "position"
      ? 1
      : isolatedStep === "profile"
      ? 0
      : null;
  const effectiveSteps = isUpdate
    ? isolatedStepIndex != null
      ? [stepEdit[isolatedStepIndex]]
      : stepEdit
    : stepAdd;
  const isLastStep = isFlatMode || isDetailInline ? true : activeStep === effectiveSteps.length - 1;

  useEffect(() => {
    if (!show) return;
    if (isolatedStepIndex != null) {
      setActiveStep(0);
      return;
    }
    if (typeof initialStep !== "number") return;
    const maxStep = isUpdate ? stepEdit.length - 1 : stepAdd.length - 1;
    const nextStep = Math.min(Math.max(initialStep, 0), maxStep);
    setActiveStep(nextStep);
  }, [show, initialStep, isUpdate, isolatedStepIndex]);

  const closeForm = () => {
    setOpenForm && setOpenForm(false);
    setIsUpdate && setIsUpdate(false);
    setActiveStep(0);
  };

  const handleCancel = () => {
    closeForm();
    onCancel && onCancel();
  };

  const { isError, mutateAsync } = useMutation(
    isUpdate ? "updateTree" : "addTree",
    isUpdate ? updateTree : createTree,
    {
      onSuccess: async (data) => {
        const updatedId = data?.id ?? formValue?.id ?? (tid != null ? Number(tid) : null);
        const patchTreeEntry = (prev) => {
          if (!prev || updatedId == null) return prev;
          if (Array.isArray(prev)) {
            return prev.map((item) =>
              Number(item?.id) === Number(updatedId) ? { ...item, ...data } : item
            );
          }
          if (Array.isArray(prev?.results)) {
            return {
              ...prev,
              results: prev.results.map((item) =>
                Number(item?.id) === Number(updatedId) ? { ...item, ...data } : item
              ),
            };
          }
          return Number(prev?.id) === Number(updatedId) ? { ...prev, ...data } : prev;
        };

        if (tid != null) {
          queryClient.setQueryData(["tree", `${tid}`], (prev) => patchTreeEntry(prev));
          queryClient.setQueryData(["tree", Number(tid)], (prev) => patchTreeEntry(prev));
          queryClient.setQueryData(["standaloneMapTree", Number(tid)], (prev) =>
            patchTreeEntry(prev)
          );
          queryClient.invalidateQueries(["tree", `${tid}`]);
          queryClient.invalidateQueries(["tree", Number(tid)]);
        }
        queryClient.setQueryData("trees", (prev) => patchTreeEntry(prev));
        queryClient.invalidateQueries("tree");
        queryClient.invalidateQueries("trees");
        queryClient.invalidateQueries("project");
        queryClient.invalidateQueries("possibleChildTrees");
        queryClient.invalidateQueries("possibleParentTrees");
        setTreeValues ? setTreeValues(data) : [];
        closeForm();
      },
      onError: (error) => {
        if (error.response && error.response.data) {
          setError(error.response.data);
        } else {
          setError({ message: "An unexpected error occurred" });
        }
      },
    }
  );

  const handleSubmit = async (values, actions) => {
    const { setSubmitting, setErrors } = actions;
    setError(undefined);
    const resolvedPosition =
      externalPosition?.lat != null && externalPosition?.lng != null
        ? {
            lat: parseFloat(externalPosition.lat),
            lng: parseFloat(externalPosition.lng),
            alt:
              externalPosition.alt != null
                ? parseFloat(externalPosition.alt)
                : values.altitude && values.altitude !== ""
                ? parseFloat(values.altitude)
                : null,
          }
        : values.latitude && values.latitude !== "" && values.longitude && values.longitude !== ""
        ? {
            lat: parseFloat(values.latitude),
            lng: parseFloat(values.longitude),
            alt: values.altitude && values.altitude !== "" ? parseFloat(values.altitude) : null,
          }
        : null;

    const formData = {
      tree_cadastre_no: values.tree_cadastre_no,
      name: values.name,
      species_id: values.species || null,
      position: resolvedPosition,

      trunk_circumference:
        values.trunk_circumference && values.trunk_circumference !== ""
          ? parseFloat(values.trunk_circumference)
          : null,
      crown_diameter:
        values.crown_diameter && values.crown_diameter !== ""
          ? parseFloat(values.crown_diameter)
          : null,
      phase: values.phase && values.phase !== "" ? parseInt(values.phase) : null,
      height: values.height && values.height !== "" ? parseFloat(values.height) : null,
      planting_year:
        values.planting_year && values.planting_year !== "" ? parseInt(values.planting_year) : null,
      soil:
        values.soil?.pwp && values.soil?.max_nf && values.soil?.max_f
          ? {
              name: values.soil.name || "",
              pwp: parseInt(values.soil.pwp) || 10,
              max_nf: parseInt(values.soil.max_nf) || 25,
              max_f: parseInt(values.soil.max_f) || 45,
            }
          : null,
    };
    const resolvedArea = values?.area || areaId;
    if (resolvedArea) {
      formData.area = { id: Number(resolvedArea) };
    }
    if (isUpdate) {
      formData["represented_trees"] = Array.isArray(values?.represented_trees)
        ? values.represented_trees
        : [];
    }
    if (!isLastStep) {
      setActiveStep(activeStep + 1);
      setSubmitting(false);
    } else {
      try {
        if (isUpdate) {
          await mutateAsync({ formData, id: formValue.id });
        } else {
          await mutateAsync(formData);
        }
        setSubmitting(false);
      } catch (error) {
        if (error.response && error.response.data) {
          setErrors(parseBackendErrors(error.response.data));
        } else {
          setErrors({ message: "An unexpected error occurred" });
        }
        setSubmitting(false);
      }
    }
  };

  const parseBackendErrors = (backendErrors) => {
    const formikErrors = {};
    if (Array.isArray(backendErrors)) {
      formikErrors["__non_field__"] = backendErrors.join(", ");
    }

    if (backendErrors.position) {
      if (backendErrors.position.lng) {
        formikErrors["position.lng"] = backendErrors.position.lng.join(", ");
      }
      if (backendErrors.position.lat) {
        formikErrors["position.lat"] = backendErrors.position.lat.join(", ");
      }
    }

    if (backendErrors.non_field_errors) {
      formikErrors["__non_field__"] = backendErrors.non_field_errors.join(", ");
    }

    return formikErrors;
  };

  const translateBackendErrorMessage = (message) => {
    return message === TREE_LIMIT_ERROR
      ? "validation.tree_limit_exceeded_for_your_package"
      : message;
  };

  // Helper function to get a top-level error message from backend errors
  const getTopLevelErrorMessage = (backendErrors) => {
    if (typeof backendErrors === "string") {
      return translateBackendErrorMessage(backendErrors);
    }

    if (Array.isArray(backendErrors)) {
      return translateBackendErrorMessage(backendErrors.join(" "));
    }

    if (backendErrors.detail) {
      return translateBackendErrorMessage(backendErrors.detail);
    }

    if (backendErrors.non_field_errors) {
      return translateBackendErrorMessage(backendErrors.non_field_errors.join(", "));
    }

    if (backendErrors.position) {
      if (backendErrors.position.lng) {
        return `Longitude: ${backendErrors.position.lng.join(", ")}`;
      }
      if (backendErrors.position.lat) {
        return `Latitude: ${backendErrors.position.lat.join(", ")}`;
      }
    }
    return "An unexpected error occurred";
  };

  const applyExternalPosition = (baseValues) => {
    if (!externalPosition) return baseValues;
    const nextLat = externalPosition.lat ?? externalPosition.latitude;
    const nextLng = externalPosition.lng ?? externalPosition.longitude;
    const nextAlt = externalPosition.alt ?? externalPosition.altitude;

    return {
      ...baseValues,
      latitude: nextLat ?? baseValues.latitude,
      longitude: nextLng ?? baseValues.longitude,
      altitude: nextAlt ?? baseValues.altitude,
    };
  };

  const resolvedInitialValues = isUpdate
    ? applyExternalPosition(formUpdateValues(formValue))
    : applyExternalPosition(initialValues || formInitialValues);

  const renderHeader = () => {
    const { title, content } = getTreeStepInfo(activeStep, isUpdate, t);
    const headerTitle =
      isolatedStep === "sensors"
        ? t("add_sensor")
        : isUpdate
        ? t("tree_form_info.edit_title")
        : `${t("addTree")}`;
    return (
      <div className="tree-form-inline-header-content">
        <h5 className="mb-0">{headerTitle}</h5>
        <div className="tree-form-inline-header-actions">
          {isInline && (
            <Button
              size="sm"
              variant="outline-secondary"
              className="tree-form-inline-cancel-button"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
          )}
          <InfoBox infoTitle={title} infoContent={content} />
        </div>
      </div>
    );
  };

  const renderBody = () => (
    <>
      {!isFlatMode && !isDetailInline && (
        <div className={styles.wizardList}>
          {(isUpdate ? effectiveSteps : stepAdd).map((step, i) => (
            <span key={step} className={activeStep == i ? "wizard-active" : ""}>
              {t(step)}
            </span>
          ))}
        </div>
      )}
      <Formik
        enableReinitialize
        initialValues={resolvedInitialValues}
        onSubmit={handleSubmit}
        validate={(values) => {
          const errors = {};
          if (!values.tree_cadastre_no?.trim() && !values.name?.trim()) {
            errors.tree_cadastre_no = "required";
          }
          if (isLastStep && !values.species?.trim()) {
            errors.species = "required";
          }
          return errors;
        }}
      >
        {({ handleSubmit, values, setFieldValue, isSubmitting }) => {
          const hasSensor =
            (Array.isArray(values.installations) && values.installations.length > 0) ||
            (Array.isArray(formValue?.installations) && formValue.installations.length > 0);
          return (
            <>
              <PositionSync position={externalPosition} />
              <PositionReporter onChange={onPositionChange} />
              <Form data-testid="tree-form" onSubmit={handleSubmit}>
                {renderTopContent ? (
                  renderTopContent({ values, setFieldValue })
                ) : topContent ? (
                  <div className="mb-3">{topContent}</div>
                ) : null}
                <div className="form-container">
                  {isDetailInline ? (
                    <TreeDetailInlineFields species={species} />
                  ) : isDetailInline ? (
                    <div className="d-flex flex-column gap-4">
                      <section>
                        <h6 className="mb-3">{t(stepAdd[0])}</h6>
                        <TreeProfile formField={formField} setFieldValue={setFieldValue} />
                      </section>
                      {/* Position is handled via the map panel in this layout */}
                      <section>
                        <h6 className="mb-3">{t(stepAdd[2])}</h6>
                        <MoreInfo formField={formField} species={species} />
                      </section>
                      <section>
                        <h6 className="mb-3">{t("soil")}</h6>
                        <Soil formField={formField} />
                      </section>
                    </div>
                  ) : isFlatMode ? (
                    <div className="d-flex flex-column gap-4">
                      <section>
                        <h6 className="mb-3">{t(stepAdd[0])}</h6>
                        <TreeProfile formField={formField} setFieldValue={setFieldValue} />
                      </section>
                      <section>
                        <h6 className="mb-3">{t(stepAdd[1])}</h6>
                        <TreeLocation
                          values={values}
                          formValue={formValue}
                          formField={formField}
                          validation={validation}
                          setFieldValue={setFieldValue}
                          readOnly={positionReadOnly}
                        />
                      </section>
                      <section>
                        <h6 className="mb-3">{t(stepAdd[2])}</h6>
                        <MoreInfo formField={formField} species={species} />
                      </section>
                      <section>
                        <h6 className="mb-3">{t("soil")}</h6>
                        <Soil formField={formField} />
                      </section>
                    </div>
                  ) : isUpdate ? (
                    renderStepEdit(
                      isolatedStepIndex != null ? isolatedStepIndex : activeStep,
                      values,
                      formValue,
                      setFieldValue,
                      species,
                      positionReadOnly
                    )
                  ) : (
                    renderStepAdd(
                      activeStep,
                      values,
                      formValue,
                      setFieldValue,
                      species,
                      positionReadOnly
                    )
                  )}
                </div>
                {hasSensor && (
                  <div className="mt-4">
                    <ChildTree
                      formField={formField}
                      values={values}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                )}
                <div className={styles.formBody}>
                  {!isFlatMode && activeStep !== 0 && (
                    <Button
                      type="button"
                      variant="btn btn-outline-primary"
                      onClick={() => setActiveStep((step) => step - 1)}
                    >
                      {t("button.back")}
                    </Button>
                  )}

                  <button type="submit" className="btn btn-outline-primary ms-auto">
                    {isLastStep
                      ? isUpdate
                        ? isSubmitting
                          ? t("button.updating")
                          : t("button.update")
                        : isSubmitting
                        ? t("button.saving")
                        : t("button.save")
                      : t("button.next")}
                  </button>
                </div>
                {isError && error && (
                  <div className={styles.validationError}>
                    <FormError title={getTopLevelErrorMessage(error) || t("error.form")} />
                  </div>
                )}
                <ErrorMessage name="latitude" component="div" className={styles.validationError} />
                <ErrorMessage name="species" component="div" className={styles.validationError} />
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );

  if (isInline) {
    return (
      <div className="tree-form-inline entity-detail-panel">
        <div className="tree-form-inline__header entity-detail-header">{renderHeader()}</div>
        <div className="tree-form-inline__body entity-detail-body">{renderBody()}</div>
      </div>
    );
  }

  return (
    <>
      <Modal
        className="tree-form-modal"
        data-testid="tree-form-modal"
        onHide={handleCancel}
        show={show}
        centered
      >
        <ModalHeader closeButton>{renderHeader()}</ModalHeader>
        <ModalBody>{renderBody()}</ModalBody>
      </Modal>
    </>
  );
}
